import React, { useMemo } from 'react';
import { sortFundNames } from '../../../../utilities/customSorting';
import { useSelector } from 'react-redux';
import { clientNameSelector } from '../../../../redux/auth/selectors';
import { raptorPlatformIdSelector } from '../../../../redux/raptorPlatform/selectors';
import GridItem from '../../../layout/GridComponents/GridItem';
import CustomTable from '../../../tables/CustomTable';
import { createSectionByIdSelector } from '../../../../redux/pages/selectors';
import {
  OverviewColumnFields,
  createGeneralTableData,
  getGeneralOverviewColumns,
} from './overview-columns';
import { Status } from '../../../../types/redux/data/dataTypes';
import OverviewSecondLevelGraphs from './OverviewSecondLevelGraphs.component';
import { mapStatusToColor } from '../../../../utilities/colorUtilities';
import NoDataMessage from '../../../feedback/NoDataMessage.component';
import { specifyStatus } from '../../../../utilities/generalMappings';
import { GeneralOverviewPageRenderedComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { useAllFundNotes } from '../../../../hooks/useFundNotes';
import { Note } from '../../../../sharedInterfaces/notes';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { RSOverviewPageModel } from '../../../../api/api.types';
import type { RiskOverviewMonitorFundRow } from '../../../../api/rs-hg/risk/riskOverview.types';
import {
  ReportsListByFund,
  useReportsListQuery,
  useRiskOverviewMonitorQuery,
} from '../../../../api/rs-hg/risk/hooks/queries';
type FundType = 'var' | 'commitment' | 'both';

export interface GeneralOverviewTableData {
  internalFundName: string;
  fundName: string;
  reports: string[];
  subFundRiskReports: string[];
  positionDate: string;
  calculationDate: string;
  currency: string;
  netAssetValue: number;
  commitmentValue: number;
  commitmentStatus: Status;
  valueAtRiskStatus: Status;
  valueAtRiskValue: number;
  varFund: string;
  varDay: string;
  ucitsLaw: Status;
  prospectusStatus: Status;
  countryExposure: any;
  positionExposure: any;
  index: number;
  derogationMessage?: string;
  timeSeriesData: any | null;
  timeSeriesStatus: any | null;
  notes: boolean;
  leverage: Status;
  fundType: FundType;
  act40Law?: Status;
  riaifLaw?: Status;
  qiaifLaw?: Status;
  nursLaw?: Status;
  fivgLaw?: Status;
  partiiLaw?: Status;
  cssfRiskSpreading?: Status;
  supplementaryRules?: Status;
  // [key: string]: any; // Any additional fields added from BE
}

type OverviewPageData = {
  overview: RSOverviewPageModel<RiskOverviewMonitorFundRow[]>;
  reports: ReportsListByFund;
  subFundRiskReports: ReportsListByFund;
};

// Temporary function to get the columns for the overview table
// Eventually this will be moved to the backend
function getOverviewColumnsFields(
  clientName: string | null,
  sectionID?: string,
): OverviewColumnFields[] | null {
  // ----------------- CLIENT SPECIFIC COLUMN CONFIGURATIONS -----------------

  if (clientName === 'pure') {
    if (sectionID === 'ucits') {
      return [
        'index',
        'internalFundName',
        'fundName',
        'reports',
        'subFundRiskReports',
        'positionDate',
        'calculationDate',
        'currency',
        'netAssetValue',
        'commitmentValue',
        'valueAtRiskValue',
        'ucitsLaw',
        'prospectusStatus',
        'notes',
      ];
    } else if (sectionID === 'fivg') {
      return [
        'index',
        'internalFundName',
        'fundName',
        'reports',
        'subFundRiskReports',
        'positionDate',
        'calculationDate',
        'currency',
        'netAssetValue',
        'commitmentValue',
        'valueAtRiskValue',
        'fivgLaw',
        'prospectusStatus',
        'notes',
      ];
    } else if (sectionID === 'partii') {
      return [
        'index',
        'internalFundName',
        'fundName',
        'reports',
        'subFundRiskReports',
        'positionDate',
        'calculationDate',
        'currency',
        'netAssetValue',
        'commitmentValue',
        'valueAtRiskValue',
        'partiiLaw',
        'prospectusStatus',
        'notes',
      ];
    } else if (sectionID === 'sif-raif') {
      return [
        'index',
        'internalFundName',
        'fundName',
        'reports',
        'subFundRiskReports',
        'positionDate',
        'currency',
        'netAssetValue',
        'leverage',
        'cssfRiskSpreading',
        'prospectusStatus',
        'notes',
      ];
    }
  }

  // ----------------- DEFAULT COLUMN CONFIGURATIONS -----------------

  if (sectionID === 'ucits') {
    return [
      'index',
      'internalFundName',
      'fundName',
      'reports',
      'positionDate',
      'calculationDate',
      'currency',
      'netAssetValue',
      'commitmentValue',
      'valueAtRiskValue',
      'ucitsLaw',
      'prospectusStatus',
      'notes',
    ];
  } else if (sectionID === 'qiaif') {
    return [
      'index',
      'internalFundName',
      'fundName',
      'reports',
      'positionDate',
      'calculationDate',
      'currency',
      'netAssetValue',
      'leverage',
      'valueAtRiskValue',
      'qiaifLaw',
      'prospectusStatus',
      'notes',
    ];
  } else if (sectionID === 'riaif') {
    return [
      'index',
      'internalFundName',
      'fundName',
      'reports',
      'positionDate',
      'calculationDate',
      'currency',
      'netAssetValue',
      'leverage',
      'valueAtRiskValue',
      'riaifLaw',
      'prospectusStatus',
      'notes',
    ];
  } else if (sectionID === 'nurs') {
    return [
      'index',
      'internalFundName',
      'fundName',
      'reports',
      'positionDate',
      'calculationDate',
      'currency',
      'netAssetValue',
      'commitmentValue',
      'valueAtRiskValue',
      'nursLaw',
      'prospectusStatus',
      'notes',
    ];
  } else if (sectionID === 'act40') {
    return [
      'index',
      'internalFundName',
      'fundName',
      'reports',
      'positionDate',
      'calculationDate',
      'currency',
      'netAssetValue',
      'commitmentValue',
      'valueAtRiskValue',
      'act40Law',
      'prospectusStatus',
      'notes',
    ];
  } else if (sectionID === 'fivg') {
    return [
      'index',
      'internalFundName',
      'fundName',
      'reports',
      'positionDate',
      'calculationDate',
      'currency',
      'netAssetValue',
      'commitmentValue',
      'valueAtRiskValue',
      'fivgLaw',
      'prospectusStatus',
      'notes',
    ];
  } else if (sectionID === 'trust') {
    return [
      'index',
      'internalFundName',
      'fundName',
      'reports',
      'positionDate',
      'calculationDate',
      'currency',
      'netAssetValue',
      'commitmentValue',
      'valueAtRiskValue',
      'prospectusStatus',
      'notes',
    ];
  } else if (sectionID === 'sif-raif') {
    return [
      'index',
      'internalFundName',
      'fundName',
      'reports',
      'positionDate',
      'currency',
      'netAssetValue',
      'leverage',
      'cssfRiskSpreading',
      'prospectusStatus',
      'notes',
    ];
  } else if (sectionID === 'partii') {
    return [
      'index',
      'internalFundName',
      'fundName',
      'reports',
      'positionDate',
      'calculationDate',
      'currency',
      'netAssetValue',
      'commitmentValue',
      'valueAtRiskValue',
      'partiiLaw',
      'prospectusStatus',
      'notes',
    ];
  } else if (sectionID === 'fil') {
    return [
      'index',
      'internalFundName',
      'fundName',
      'reports',
      'positionDate',
      'calculationDate',
      'currency',
      'netAssetValue',
      'commitmentValue',
      'valueAtRiskValue',
      'prospectusStatus',
      'notes',
    ];
  }

  return null;
}

function addSupplementaryRulesColumn(
  fields: OverviewColumnFields[] | null,
  overviewFunds: RiskOverviewMonitorFundRow[],
): OverviewColumnFields[] | null {
  if (!fields) return fields;
  const supplementaryRulesField = 'supplementaryRules' as OverviewColumnFields;

  const hasSupplementaryRulesData = overviewFunds.some(
    (fund) =>
      specifyStatus(String(fund.monitor_data?.Supplementary_Rules ?? '')) !==
      Status.NA,
  );

  if (!hasSupplementaryRulesData) {
    return fields.filter((field) => field !== supplementaryRulesField);
  }

  if (fields.includes(supplementaryRulesField)) return fields;
  const idx = fields.indexOf('prospectusStatus');
  if (idx === -1) return [...fields, supplementaryRulesField];
  return [
    ...fields.slice(0, idx + 1),
    supplementaryRulesField,
    ...fields.slice(idx + 1),
  ];
}

const OverviewPage: React.FC<GeneralOverviewPageRenderedComponentProps> = (
  props,
) => {
  const sectionDetails = useSelector(createSectionByIdSelector(props.section));
  const fundTypes = sectionDetails?.fundTypes;
  const clientName = useSelector(clientNameSelector);
  const selectedRaptorPlatformId = useSelector(raptorPlatformIdSelector);
  const includeSubFundRiskReports = clientName?.includes('pure') ?? false;

  const reportsListQuery = useReportsListQuery({
    fundTypes,
    raptorPlatformId: selectedRaptorPlatformId,
  });


  const overviewQuery = useRiskOverviewMonitorQuery({
    positionDate: props.positionDate,
    fundTypes,
    raptorPlatformId: selectedRaptorPlatformId,
  });

  const subFundRiskReportsQuery = useReportsListQuery(
    {
      fundTypes,
      raptorPlatformId: selectedRaptorPlatformId,
      reportType: 'sub_fund_risk_report',
    },
    { enabled: includeSubFundRiskReports },
  );

  const pageData = useMemo((): OverviewPageData | null => {
    if (!overviewQuery.data?.data) {
      return null;
    }

    const overviewRows = [...overviewQuery.data.data].sort((a, b) =>
      sortFundNames(a.fund_name || '', b.fund_name || ''),
    );

    return {
      overview: {
        ...overviewQuery.data,
        data: overviewRows,
      },
      reports: reportsListQuery.data ?? {},
      subFundRiskReports: subFundRiskReportsQuery.data ?? {},
    };
  }, [
    overviewQuery.data,
    reportsListQuery.data,
    subFundRiskReportsQuery.data,
  ]);

  const overviewRows = overviewQuery.data?.data ?? [];

  const fundNames = overviewRows.map((fund) => fund.fund_name);

  const positionDates = overviewRows
    .map((fund) => fund.monitor_data?.selected_position_date)
    .filter((d): d is string => !!d);

  const sortedDates = [...positionDates].sort();
  const earliestDate = sortedDates[0];
  const latestDate = sortedDates[sortedDates.length - 1];

  const { fundNotesData, loading: preFetchedNotesLoading } = useAllFundNotes({
    fundNames,
    date1: earliestDate || '',
    date2: latestDate,
    subject: undefined,
    topic: undefined,
    shouldCall: !!earliestDate && fundNames.length > 0,
  });

  const notesByFundAndDate = useMemo(() => {
    const map = new Map<string, Note[]>();
    Object.entries(fundNotesData).forEach(([fundName, notes]) => {
      notes.forEach((note: Note) => {
        const key = `${fundName}-${note.fund_note_position_date}`;
        const existing = map.get(key) || [];
        existing.push(note);
        map.set(key, existing);
      });
    });
    return map;
  }, [fundNotesData]);

  const getNotesForFund = (fundName: string, positionDate: string): Note[] => {
    const key = `${fundName}-${positionDate}`;
    return notesByFundAndDate.get(key) || [];
  };

  const tableHeight = 'calc(100vh - 15.62rem)';

  return (
    <QueryBoundary<OverviewPageData>
      isLoading={overviewQuery.isLoading}
      isFetching={overviewQuery.isFetching}
      isError={!!overviewQuery.error}
      error={overviewQuery.error}
      data={pageData}
      loadingMessages={['Loading Overview Data...']}
      errorMessage="No Data Available."
      noDataMessage="No Data Available."
    >
      {({ overview, reports, subFundRiskReports }) => {
        const columnsFields = addSupplementaryRulesColumn(
          getOverviewColumnsFields(clientName, sectionDetails?.sectionId),
          overview.data,
        );

        if (!columnsFields) {
          return (
            <NoDataMessage
              message={`No configuration found for the section ${sectionDetails?.sectionId}`}
            />
          );
        }

        const columns = getGeneralOverviewColumns(
          clientName,
          columnsFields,
          props.section,
          getNotesForFund,
          preFetchedNotesLoading,
        );

        const dataPreparedForTable = createGeneralTableData(
          overview.data,
          reports,
          subFundRiskReports,
          clientName,
          props.section,
        );

        const overviewCSVFields = columnsFields.filter(
          (field: OverviewColumnFields) =>
            field !== 'reports' && field !== 'index',
        );

        const csvFieldMap = columns
          .filter((col) => col.field !== 'reports' && col.field !== 'index')
          .map((column) => ({
            key: column.field as string,
            label: column.title as string,
          }));

        return (
          <GridItem card xs={12}>
            <CustomTable<GeneralOverviewTableData>
              csvFields={overviewCSVFields}
              fieldsMap={csvFieldMap}
              showToolbar
              loadingHeight={tableHeight}
              title={`${sectionDetails?.sectionTitle.toUpperCase()} Overview Data`}
              columns={columns}
              data={dataPreparedForTable}
              detailPanel={[
                {
                  disabled: false,
                  render: (rowData: GeneralOverviewTableData) => (
                    <OverviewSecondLevelGraphs
                      fundName={rowData.internalFundName}
                      positionDate={rowData.positionDate}
                      varFund={rowData.varFund === 'yes'}
                      passValue={mapStatusToColor(
                        specifyStatus(rowData.timeSeriesStatus),
                      )}
                    />
                  ),
                },
              ]}
              options={{
                pageSize: 100,
                emptyRowsWhenPaging: false,
                pageSizeOptions: [50, 100, 200, 500, dataPreparedForTable.length],
                search: true,
                exportButton: true,
                rowStyle: {
                  padding: '0.62rem',
                },
                raptorExport: true,
                detailPanelColumnStyle: {
                  width: '0.5rem',
                  minWidth: '0.5rem',
                },
              }}
            />
          </GridItem>
        );
      }}
    </QueryBoundary>
  );
};

export default OverviewPage;
